<template>
  <div
    ref="rightButtonRef"
    class="rteao-monitor-right-button-wrap"
    :class="{ expand: isExpand, 'show-sub-menu': !!activeMenu }"
    :style="[subPanelStyle]"
  >
    <div class="toggle-button" @click="toggle">
      <i class="el-icon-s-fold" v-if="isExpand"></i>
      <i class="el-icon-s-unfold" v-else></i>
    </div>

    <div class="menu-list">
      <template v-for="menu in menuList">
        <div
          class="menu-item"
          :class="{ active: menu === activeMenu }"
          v-if="isShow(menu)"
          :key="menu.id"
          @click="open(menu)"
        >
          <i :class="['iconfont', menu.icon]"></i>
          <span class="text">{{ menu.name }}</span>
        </div>
      </template>

      <span class="tips">{{ tips }}</span>
    </div>

    <div class="sub-panel" v-if="activeMenu">
      <span class="sub-panel-title">{{ activeMenu.name }}</span>

      <div class="sub-panel-content" :style="activeMenu.contentStyle">
        <MForm
          v-if="activeMenu.type !== 'table'"
          :key="activeMenu.name"
          v-bind="form"
          :options="activeMenu.options"
        />
        <MTable
          v-else
          :key="activeMenu.id"
          v-bind="table"
          v-on:search="getNodeDetectList"
          :options="activeMenu.options"
        />
      </div>
    </div>
  </div>
</template>

<script>
import MForm from '@/components/form/v2.vue';
import MTable from '@/components/mTable/m-table.vue';
import { useClickOutsideEvent } from '@/utils/dom/event';
import { getNodeDetectList } from '@/api/RTEAO';
import { formatTime, getTimestampRange } from '@/utils/date';
import { isFunction } from '@/utils/is';
import { sleep } from '@/utils/utils';

export default {
  inject: [
    'pathogenList',
    'model',
    'nodeList',
    'nodeRunStateList',
    'nodeListStateGroup',
    'timeRangeDict',
    'nodeSceneTypeList',
    'nodeSceneTypeGroup'
  ],
  components: {
    MTable,
    MForm
  },
  props: {
    mode: {
      type: String,
      default: 'monitor'
    }
  },
  data() {
    return {
      isExpand: this.mode === 'review',
      activeMenu: null,
      old: null,
      form: {
        config: {},
        model: this.model,
        dataSource: {
          nodeList: this.nodeList,
          pathogenList: this.pathogenList
        }
      },
      table: {
        loading: false,
        key: 'table1',
        gutterWidth: 8,
        height: '100%',
        tableData: [],
        pagiOption: {
          offset: 1,
          limit: 10,
          total: 0,
          pagiSize: [10, 15, 20, 30],
          layout: 'total, sizes, prev, pager, next, jumper'
        },
        tableColumn: [
          {
            label: '采样开始时间',
            prop: 'sampleStartTime',
            temp: ({ row }) => formatTime(row.sampleStartTime, 'yyyy-MM-dd hh:mm')
          },
          {
            label: '采样结束时间',
            prop: 'sampleEndTime',
            temp: ({ row }) => formatTime(row.sampleEndTime, 'yyyy-MM-dd hh:mm')
          },
          {
            label: '节点',
            prop: 'officeName'
          },
          {
            label: '哨点',
            prop: 'name'
          },
          {
            label: '病毒名称',
            prop: 'pathogenName'
          },
          {
            label: 'CT值',
            prop: 'pathogenValue',
            width: '120px',
            temp: ({ row }) => (row.pathogenValue >= 40 ? 'UD' : row.pathogenValue)
          },
          {
            label: '检测结果',
            prop: 'pathogenState',
            width: '140px',
            temp: ({ row }) => {
              return row.pathogenState == '1' ? '阳性' : '阴性';
            }
          }
        ]
      },
      menuList: [
        {
          id: 'timeRangeFlag',
          name: '时间范围',
          icon: 'icon-clock',
          show: () => this.mode !== 'review',
          subPanelStyle: {
            '--sub-panel-height': '100%'
          },
          options: [
            {
              prop: 'timeRangeFlag',
              type: 'radio',
              componentProps: {
                options: Object.values(this.timeRangeDict)
              },
              on: {
                change: e => {
                  this.model.timeRange.splice(0, Infinity, ...getTimestampRange(e));
                }
              }
            }
          ]
        },
        {
          id: 'timeRange',
          name: '时间范围',
          icon: 'icon-clock',
          show: () => false,
          // show: () => this.mode === 'review',
          contentStyle: {
            overflow: 'unset'
          },
          subPanelStyle: {
            '--sub-panel-width': '450px',
            '--sub-panel-height': '30%'
          },
          options: [
            {
              prop: 'timeRange',
              type: 'datePicker',
              componentAttrs: {
                style: 'width: 100%'
              },
              componentProps: {
                appendToBody: false,
                type: 'monthrange',
                rangeSeparator: '-',
                startPlaceholder: '开始时间',
                endPlaceholder: '结束时间',
                valueFormat: 'timestamp'
              }
            }
          ]
        },
        {
          id: 'region',
          name: '节点分类',
          show: () => this.mode !== 'review',
          icon: 'icon-pie',
          subPanelStyle: {},
          options: [
            {
              prop: 'nodeSceneTypeCheckAll',
              type: 'checkbox',
              on: {
                change: v => {
                  if (v) {
                    this.form.model.nodeSceneTypeList.splice(
                      0,
                      Infinity,
                      ...this.nodeSceneTypeList.map(e => e.value)
                    );
                  } else {
                    this.form.model.nodeSceneTypeList.splice(0, Infinity);
                  }
                }
              },
              componentProps: {
                label: '全选',
                indeterminate: ({ nodeSceneTypeList }) => {
                  return (
                    nodeSceneTypeList.length > 0 &&
                    nodeSceneTypeList.length < this.nodeSceneTypeList.length
                  );
                }
              }
            },
            {
              prop: 'nodeSceneTypeList',
              type: 'checkboxGroup',
              componentProps: {
                options: this.nodeSceneTypeList.map(e => ({
                  ...e,
                  indeterminate: ({ value }, { nodeList }) => {
                    const list = this.nodeSceneTypeGroup[value].items;

                    const len = list.filter(e => nodeList.includes(e.id)).length;

                    return len > 0 && len < list.length;
                  }
                }))
              }
            }
          ]
        },
        {
          id: 'nodeStatus',
          name: '节点状态',
          show: () => this.mode !== 'review',
          icon: 'icon-node-state',
          subPanelStyle: {
            '--sub-panel-height': '60%'
          },
          options: [
            {
              prop: 'nodeStateCheckAll',
              type: 'checkbox',
              on: {
                change: v => {
                  if (v) {
                    this.form.model.nodeRunStateList.splice(
                      0,
                      Infinity,
                      ...this.nodeRunStateList.map(e => e.state)
                    );
                  } else {
                    this.form.model.nodeRunStateList.splice(0, Infinity);
                  }
                }
              },
              componentProps: {
                label: '全选',
                indeterminate: ({ nodeRunStateList }) => {
                  return (
                    nodeRunStateList.length > 0 &&
                    nodeRunStateList.length < this.nodeRunStateList.length
                  );
                }
              }
            },
            {
              prop: 'nodeRunStateList',
              type: 'checkboxGroup',
              dataKey: 'nodeRunStateList',
              componentProps: {
                options: this.nodeRunStateList.map(e => ({
                  label: e.text,
                  value: e.state,
                  indeterminate: ({ value }, { nodeList }) => {
                    const list = this.nodeListStateGroup[value].items;

                    const len = list.filter(e => nodeList.includes(e.id)).length;

                    return len > 0 && len < list.length;
                  }
                }))
              }
            }
          ]
        },
        {
          id: 'nodeSelection',
          name: '节点名称',
          icon: 'icon-select-node',
          show: () => this.mode !== 'review',
          subPanelStyle: {
            '--sub-panel-width': '15vw'
          },
          options: [
            {
              prop: 'nodeListCheckAll',
              type: 'checkbox',
              on: {
                change: v => {
                  if (v) {
                    this.form.model.nodeList.splice(0, Infinity, ...this.nodeList.map(e => e.id));
                  } else {
                    this.form.model.nodeList.splice(0, Infinity);
                  }
                }
              },
              componentProps: {
                label: '全选',
                indeterminate: ({ nodeList }) => {
                  return nodeList.length > 0 && nodeList.length < this.nodeList.length;
                }
              }
            },
            {
              prop: 'nodeList',
              type: 'checkboxGroup',
              dataKey: 'nodeList',
              componentProps: {
                props: {
                  label: 'name',
                  value: 'id'
                }
              }
            }
          ]
        },
        {
          id: 'pathogenSelection',
          name: '病原体',
          icon: 'icon-pathogen',
          show: () => this.mode !== 'review',
          subPanelStyle: {},
          options: [
            {
              prop: 'pathogenListCheckAll',
              type: 'checkbox',
              on: {
                change: v => {
                  if (v) {
                    this.form.model.pathogenList.splice(
                      0,
                      Infinity,
                      ...this.pathogenList.map(e => e.pathogenType)
                    );
                  } else {
                    this.form.model.pathogenList.splice(0, Infinity);
                  }
                }
              },
              componentProps: {
                label: '全选',
                indeterminate: ({ pathogenList }) => {
                  return pathogenList.length > 0 && pathogenList.length < this.pathogenList.length;
                }
              }
            },
            {
              prop: 'pathogenList',
              type: 'checkboxGroup',
              dataKey: 'pathogenList',
              componentProps: {
                props: {
                  label: 'pathogenTypeName',
                  value: 'pathogenType'
                }
              }
            }
          ]
        },
        {
          id: 'detailList',
          name: '详情列表',
          icon: 'icon-list',
          type: 'table',
          subPanelStyle: {
            '--sub-panel-width': '80vw'
          }
        }
      ]
    };
  },
  computed: {
    subPanelStyle() {
      return this.activeMenu?.subPanelStyle || {};
    },
    tips() {
      // ? this.model.timeRange.map(e => formatTime(e, 'yyyy-MM-dd')).join(' 至 ')
      const text = this.mode === 'review' ? '' : this.timeRangeDict[this.model.timeRangeFlag].label;
      return text;
    }
  },
  methods: {
    isShow(e) {
      if (!('show' in e)) return true;

      return isFunction(e.show) ? e.show() : e.show;
    },
    toggle() {
      this.isExpand = !this.isExpand;

      if (!this.isExpand) this.activeMenu = null;
    },
    open(menu) {
      if (this.activeMenu === menu) {
        this.activeMenu = null;
        return;
      }

      this.activeMenu = menu;
    },
    close() {
      if (this.isExpand) {
        this.toggle();
      }
    },
    async getNodeDetectList() {
      try {
        this.table.loading = true;

        const { offset, limit } = this.table.pagiOption;

        const { nodeList, pathogenList } = this.model;

        await sleep(500);

        const res = await getNodeDetectList({
          offset,
          limit,
          officeIds: nodeList,
          pathogenTypes: pathogenList
        });

        if (res.code !== 200) return;

        this.table.tableData.splice(0, Infinity, ...res.data.list);
        this.table.pagiOption.total = res.data.total;
      } catch (error) {
        console.log(error);
      } finally {
        this.table.loading = false;
      }
    }
  },
  watch: {
    activeMenu(n) {
      if (n?.id !== 'detailList') return;

      this.getNodeDetectList();
    }
  },
  async mounted() {
    this.clearEvent = useClickOutsideEvent({
      el: this.$refs.rightButtonRef,
      handler: () => {
        this.close();
      }
    });
  },
  beforeDestroy() {
    this.clearEvent?.();
  }
};
</script>

<style lang="scss">
.rteao-monitor-right-button-wrap {
  --base-size: 1vw;

  --toggle-btn-height: 40px;
  --panel-width: 12.86rem;
  --panel-height: calc(100vh - 60px - 80px - 60px);
  --sub-panel-width: 12.86rem;
  --sub-panel-height: var(--panel-height);

  font-size: var(--base-size);

  position: absolute;
  right: 10px;
  top: calc(var(--header-height) + var(--gap) * 2);
  z-index: 999;

  width: var(--toggle-btn-height);
  height: var(--toggle-btn-height);

  transition: all 0.3s ease-in-out;

  overflow: hidden;

  .menu-list {
    width: var(--panel-width);
    height: var(--panel-height);
    border-radius: 10px 10px 10px 10px;
    border: 2px solid #047fbc;
    box-sizing: border-box;
    box-shadow: inset 4px 4px 40px 0px rgba(0, 112, 248, 0.3), 0px 0px 6px 0px rgba(0, 0, 0, 0.25);
    background: #112132;
    opacity: 0;
    padding-top: var(--toggle-btn-height);

    position: absolute;
    right: 0;
    top: 0;

    display: flex;
    flex-direction: column;
    gap: var(--gap);

    transition: opacity 0.3s ease-in-out;

    .menu-item {
      display: flex;
      align-items: center;
      // height: 3.6rem;

      font-family: Source Han Sans CN, Source Han Sans CN;

      padding: 1rem 1.5rem;

      color: #ffffff;

      cursor: pointer;

      transition: all 0.3s ease-in-out;

      &:hover,
      &.active {
        background: #02364b;
        color: #05eeff;
      }

      .iconfont {
        font-size: 1.75rem;

        margin-right: 10px;
      }

      .icon-pie {
        font-size: 1.4rem;
      }

      span {
        font-weight: 400;
        font-size: 1.35rem;
        line-height: 1;
        align-self: center;
      }
    }

    .tips {
      font-weight: 500;
      font-size: 1.35rem;
      color: #3997ea;
      text-align: center;

      position: absolute;
      left: 0;
      bottom: var(--gap);

      width: 100%;
    }
  }

  &.expand {
    width: var(--panel-width);
    height: var(--panel-height);

    // overflow: unset;

    .menu-list {
      opacity: 1;
    }
  }

  &.show-sub-menu {
    width: calc(var(--panel-width) + var(--gap) + var(--sub-panel-width));

    .sub-panel {
      opacity: 1;
    }
  }

  .sub-panel {
    width: var(--sub-panel-width);
    height: var(--sub-panel-height);

    border-radius: 10px 10px 10px 10px;
    border: 2px solid #047fbc;
    box-sizing: border-box;
    box-shadow: inset 4px 4px 40px 0px rgba(0, 112, 248, 0.3), 0px 0px 6px 0px rgba(0, 0, 0, 0.25);
    background: #112132;
    opacity: 0;

    position: absolute;
    right: calc(var(--panel-width) + var(--gap));
    top: 0;

    opacity: 0;

    transition: all 0.3s ease-in-out;

    &-title {
      width: 100%;
      display: inline-block;
      height: 3.645rem;
      line-height: 3.645rem;
      box-sizing: border-box;
      border-radius: 10px 10px 0 0;

      font-weight: 500;
      font-size: 1.35rem;

      text-align: center;

      color: #047fd7;
      background: #02364b;
    }

    &-content {
      height: calc(100% - 3.645rem);

      overflow-y: auto;
      @include scrollar-style-thin;

      .el-date-editor {
        height: 2rem;
        line-height: 2rem;

        .el-range__icon {
          font-size: 0.8rem;
        }

        .el-range-input {
          font-size: 1rem;
        }

        .el-month-table {
          font-size: 0.8rem;
        }

        .el-date-range-picker__header {
          .el-picker-panel__icon-btn {
            font-size: 0.8rem;
          }

          div {
            font-size: 1rem;
          }
        }
      }

      .m-table {
        width: calc(var(--sub-panel-width) - 4px);
        box-sizing: border-box;
        height: 100%;

        .el-table {
          height: calc(100% - 80px) !important;
          max-height: calc(100% - 80px) !important;

          &__body-wrapper {
            @include scrollar-style-thin;
          }
        }
      }
    }
  }

  .toggle-button {
    font-size: 1.5rem;
    height: var(--toggle-btn-height);
    line-height: var(--toggle-btn-height);

    position: absolute;
    right: var(--gap);
    top: 0;
    z-index: 2;

    cursor: pointer;
  }

  .custom-form {
    .el-radio-group {
      display: flex;
      flex-direction: column;
      gap: var(--gap);
      // padding: 1.3vw 1vw;

      .el-radio {
        height: 2rem;
        line-height: 2rem;
        font-size: 1.35rem;

        &__label {
          font-size: 1.35rem;
        }

        .el-radio__inner {
          width: 1.25rem;
          height: 1.25rem;

          &::after {
            width: 0.4rem;
            height: 0.4rem;
          }
        }
      }
    }

    .el-form-item {
      padding: 1rem 1rem 0;

      margin-bottom: 0;
    }

    .el-radio__inner {
      border: 1px solid #078ca3;
    }

    .el-checkbox-group {
      display: flex;
      flex-direction: column;
      gap: var(--gap);
    }

    .el-checkbox {
      height: 2rem;
      line-height: 2rem;
      font-size: 1.35rem;

      &__label {
        font-size: 1.35rem;
      }

      .el-checkbox__inner {
        width: 1.25rem;
        height: 1.25rem;
        border: 1px solid #078ca3;

        &::before {
          top: 48%;
        }

        &::after {
          left: 32%;
          top: 15%;
          width: 0.26rem;
          height: 0.5rem;

          border: 3px solid #0c1620;
          border-left: 0;
          border-top: 0;
        }
      }
    }
  }
}
</style>
